import { useState } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { BACKEND_URL } from '@/config/api';

export const VoteButtons = ({ 
  whiteboardId, 
  objectId, 
  votes = { up: [], down: [] },
  currentUserId,
  token,
  onVoteUpdate,
  compact = false,
  vertical = false
}) => {
  const [isVoting, setIsVoting] = useState(false);
  const [localVotes, setLocalVotes] = useState(votes);

  const upvotes = localVotes.up?.length || 0;
  const downvotes = localVotes.down?.length || 0;
  const score = upvotes - downvotes;
  const userVote = localVotes.up?.includes(currentUserId) 
    ? 'up' 
    : localVotes.down?.includes(currentUserId) 
      ? 'down' 
      : null;

  const handleVote = async (voteType) => {
    if (isVoting || !token) return;

    setIsVoting(true);
    
    // Optimistic update
    const newVotes = { ...localVotes };
    if (voteType === 'up') {
      if (userVote === 'up') {
        newVotes.up = newVotes.up.filter(id => id !== currentUserId);
      } else {
        newVotes.up = [...(newVotes.up || []), currentUserId];
        newVotes.down = (newVotes.down || []).filter(id => id !== currentUserId);
      }
    } else {
      if (userVote === 'down') {
        newVotes.down = newVotes.down.filter(id => id !== currentUserId);
      } else {
        newVotes.down = [...(newVotes.down || []), currentUserId];
        newVotes.up = (newVotes.up || []).filter(id => id !== currentUserId);
      }
    }
    setLocalVotes(newVotes);

    try {
      const response = await fetch(
        `${BACKEND_URL}/api/whiteboards/${whiteboardId}/objects/${objectId}/vote`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ vote_type: voteType })
        }
      );

      if (response.ok) {
        const data = await response.json();
        setLocalVotes(data.votes);
        onVoteUpdate?.(objectId, data.votes);
      }
    } catch (error) {
      console.error('Error voting:', error);
      // Revert on error
      setLocalVotes(votes);
    } finally {
      setIsVoting(false);
    }
  };

  // Update local votes when prop changes (from WebSocket)
  if (JSON.stringify(votes) !== JSON.stringify(localVotes) && !isVoting) {
    setLocalVotes(votes);
  }

  // Vertical layout for right edge positioning
  if (vertical) {
    return (
      <div 
        className="flex flex-col items-center gap-0.5 bg-white/95 backdrop-blur-sm rounded-lg shadow-md border border-gray-200 p-1"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => handleVote('up')}
          className={`p-1.5 rounded transition-colors ${
            userVote === 'up' 
              ? 'bg-green-500 text-white' 
              : 'hover:bg-green-100 text-gray-500'
          }`}
          disabled={isVoting}
          data-testid={`vote-up-${objectId}`}
        >
          <ThumbsUp size={12} className={userVote === 'up' ? 'fill-current' : ''} />
        </button>
        <span className={`text-[10px] font-bold leading-none ${
          score > 0 ? 'text-green-600' : score < 0 ? 'text-red-600' : 'text-gray-400'
        }`}>
          {score}
        </span>
        <button
          onClick={() => handleVote('down')}
          className={`p-1.5 rounded transition-colors ${
            userVote === 'down' 
              ? 'bg-red-500 text-white' 
              : 'hover:bg-red-100 text-gray-500'
          }`}
          disabled={isVoting}
          data-testid={`vote-down-${objectId}`}
        >
          <ThumbsDown size={12} className={userVote === 'down' ? 'fill-current' : ''} />
        </button>
      </div>
    );
  }

  if (compact) {
    return (
      <div className="flex items-center gap-1 text-xs">
        <button
          onClick={(e) => { e.stopPropagation(); handleVote('up'); }}
          className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded transition-colors ${
            userVote === 'up' 
              ? 'bg-green-100 text-green-600' 
              : 'hover:bg-gray-100 text-gray-500'
          }`}
          disabled={isVoting}
        >
          <ThumbsUp size={12} className={userVote === 'up' ? 'fill-current' : ''} />
          {upvotes > 0 && <span>{upvotes}</span>}
        </button>
        <button
          onClick={(e) => { e.stopPropagation(); handleVote('down'); }}
          className={`flex items-center gap-0.5 px-1.5 py-0.5 rounded transition-colors ${
            userVote === 'down' 
              ? 'bg-red-100 text-red-600' 
              : 'hover:bg-gray-100 text-gray-500'
          }`}
          disabled={isVoting}
        >
          <ThumbsDown size={12} className={userVote === 'down' ? 'fill-current' : ''} />
          {downvotes > 0 && <span>{downvotes}</span>}
        </button>
      </div>
    );
  }

  return (
    <div 
      className="flex items-center gap-2 bg-white/90 backdrop-blur-sm rounded-full px-2 py-1 shadow-md border border-gray-200"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        onClick={() => handleVote('up')}
        className={`flex items-center gap-1 px-2 py-1 rounded-full transition-all ${
          userVote === 'up' 
            ? 'bg-green-500 text-white' 
            : 'hover:bg-green-100 text-gray-600 hover:text-green-600'
        }`}
        disabled={isVoting}
        data-testid={`vote-up-${objectId}`}
      >
        <ThumbsUp size={14} className={userVote === 'up' ? 'fill-current' : ''} />
        <AnimatePresence mode="wait">
          <motion.span
            key={upvotes}
            initial={{ y: -10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 10, opacity: 0 }}
            className="text-xs font-medium min-w-[16px] text-center"
          >
            {upvotes}
          </motion.span>
        </AnimatePresence>
      </button>

      {/* Score indicator */}
      <div className={`text-xs font-bold min-w-[24px] text-center ${
        score > 0 ? 'text-green-600' : score < 0 ? 'text-red-600' : 'text-gray-400'
      }`}>
        {score > 0 ? `+${score}` : score}
      </div>

      <button
        onClick={() => handleVote('down')}
        className={`flex items-center gap-1 px-2 py-1 rounded-full transition-all ${
          userVote === 'down' 
            ? 'bg-red-500 text-white' 
            : 'hover:bg-red-100 text-gray-600 hover:text-red-600'
        }`}
        disabled={isVoting}
        data-testid={`vote-down-${objectId}`}
      >
        <ThumbsDown size={14} className={userVote === 'down' ? 'fill-current' : ''} />
        <AnimatePresence mode="wait">
          <motion.span
            key={downvotes}
            initial={{ y: -10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 10, opacity: 0 }}
            className="text-xs font-medium min-w-[16px] text-center"
          >
            {downvotes}
          </motion.span>
        </AnimatePresence>
      </button>
    </div>
  );
};

// Hook to handle WebSocket vote updates
export const useVoteUpdates = (objects, setObjects) => {
  const handleVoteUpdate = (objectId, votes) => {
    setObjects(prev => prev.map(obj => 
      obj.id === objectId ? { ...obj, votes } : obj
    ));
  };

  return handleVoteUpdate;
};
